/**
 * The canonical Gen 6+ type-effectiveness chart (real multipliers) + a lookup
 * helper for (possibly dual-typed) defenders. Data lives here; the frozen *shape*
 * (TypeChart) lives in @/shared.
 */
import type { PokemonType, TypeChart, TypeMultiplier } from '@/shared';
import { POKEMON_TYPES } from '@/shared';

// Sparse spec: [attacking][defending] = multiplier, where anything unlisted = 1.
// Source: the standard, freely-documented Gen 6+ type chart.
const OVERRIDES: Partial<Record<PokemonType, Partial<Record<PokemonType, TypeMultiplier>>>> = {
  normal: { rock: 0.5, ghost: 0, steel: 0.5 },
  fire: { fire: 0.5, water: 0.5, grass: 2, ice: 2, bug: 2, rock: 0.5, dragon: 0.5, steel: 2 },
  water: { fire: 2, water: 0.5, grass: 0.5, ground: 2, rock: 2, dragon: 0.5 },
  electric: { water: 2, electric: 0.5, grass: 0.5, ground: 0, flying: 2, dragon: 0.5 },
  grass: {
    fire: 0.5,
    water: 2,
    grass: 0.5,
    poison: 0.5,
    ground: 2,
    flying: 0.5,
    bug: 0.5,
    rock: 2,
    dragon: 0.5,
    steel: 0.5,
  },
  ice: { fire: 0.5, water: 0.5, grass: 2, ice: 0.5, ground: 2, flying: 2, dragon: 2, steel: 0.5 },
  fighting: {
    normal: 2,
    ice: 2,
    poison: 0.5,
    flying: 0.5,
    psychic: 0.5,
    bug: 0.5,
    rock: 2,
    ghost: 0,
    dark: 2,
    steel: 2,
    fairy: 0.5,
  },
  poison: { grass: 2, poison: 0.5, ground: 0.5, rock: 0.5, ghost: 0.5, steel: 0, fairy: 2 },
  ground: {
    fire: 2,
    electric: 2,
    grass: 0.5,
    poison: 2,
    flying: 0,
    bug: 0.5,
    rock: 2,
    steel: 2,
  },
  flying: { electric: 0.5, grass: 2, fighting: 2, bug: 2, rock: 0.5, steel: 0.5 },
  psychic: { fighting: 2, poison: 2, psychic: 0.5, dark: 0, steel: 0.5 },
  bug: {
    fire: 0.5,
    grass: 2,
    fighting: 0.5,
    poison: 0.5,
    flying: 0.5,
    psychic: 2,
    ghost: 0.5,
    dark: 2,
    steel: 0.5,
    fairy: 0.5,
  },
  rock: { fire: 2, ice: 2, fighting: 0.5, ground: 0.5, flying: 2, bug: 2, steel: 0.5 },
  ghost: { normal: 0, psychic: 2, ghost: 2, dark: 0.5 },
  dragon: { dragon: 2, steel: 0.5, fairy: 0 },
  dark: { fighting: 0.5, psychic: 2, ghost: 2, dark: 0.5, fairy: 0.5 },
  steel: { fire: 0.5, water: 0.5, electric: 0.5, ice: 2, rock: 2, steel: 0.5, fairy: 2 },
  fairy: { fire: 0.5, fighting: 2, poison: 0.5, dragon: 2, dark: 2, steel: 0.5 },
};

function buildChart(): TypeChart {
  const chart = {} as Record<PokemonType, Record<PokemonType, TypeMultiplier>>;
  for (const atk of POKEMON_TYPES) {
    const row = {} as Record<PokemonType, TypeMultiplier>;
    for (const def of POKEMON_TYPES) {
      row[def] = OVERRIDES[atk]?.[def] ?? 1;
    }
    chart[atk] = row;
  }
  return chart;
}

/** The frozen, fully-populated Gen 6+ chart. */
export const TYPE_CHART: TypeChart = buildChart();

/** Multiplier of an attacking type against a single defending type. */
export function typeMultiplier(attacking: PokemonType, defending: PokemonType): TypeMultiplier {
  return TYPE_CHART[attacking][defending];
}

/**
 * Combined effectiveness of an attacking type vs a (possibly dual-typed) defender.
 * Result ∈ {0, 0.25, 0.5, 1, 2, 4}.
 */
export function effectivenessAgainst(
  attacking: PokemonType,
  defenders: readonly [PokemonType] | readonly [PokemonType, PokemonType],
): number {
  let mult = 1;
  for (const def of defenders) {
    mult *= TYPE_CHART[attacking][def];
  }
  return mult;
}
